<template>
  <div class="chemtracker-module">
    <div v-if="isBusy" class="text-center text-danger my-2">
      <raft-busy busy-text="Fetching SDS search results..." />
    </div>
    <b-form v-else>
      <b-container fluid class="sds-results-table-container">
        <b-table
          striped
          hover
          show-empty
          id="sds-search-results"
          class="sds-search-results"
          caption-top
          caption-html="Selected SDS and Central Database Options"
          :fields="fields"
          :items="sdsSearchResults"
          ref="table"
        >
          <template #empty="scope">
            <div class="chemtracker-empty-text">
              No SDS results found. Select from the attachment options below.
            </div>
          </template>
          <template #cell(selected)="row">
            <b-form-group>
              <input
                type="radio"
                :class="
                  'btn-group-toggle selection-' + row.item.cached_entity_type
                "
                :name="'sds-result-name-' + getItemUniqueId(row.item)"
                :id="'sds-result-' + getItemUniqueId(row.item)"
                :value="getItemUniqueId(row.item)"
                :data-entity-uuid="row.item.cached_entity_uuid"
                :data-entity-type="row.item.cached_entity_type"
                v-model="selectedSdsResult"
                @change="
                  setSelectedSdsDocumentId(
                    row.item.id,
                    row.item.cached_entity_uuid,
                    row.item,
                  )
                "
              />
            </b-form-group>
          </template>
          <template #cell(view_url)="row">
            <div
              v-if="linkLoading[getItemUniqueId(row.item)]"
              class="text-center text-danger my-2"
            >
              <raft-busy busy-text="Fetching SDS file..." />
            </div>
            <div v-else>
              <a
                v-if="row.item.origin === 'external'"
                href="#"
                @click.prevent="sdsDownloadFile(row.item)"
                target="null"
                >View</a
              >
              <a v-else :href="row.item.url + getJwt(row.item)" target="_blank"
                >View</a
              >
            </div>
          </template>
        </b-table>
      </b-container>
    </b-form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import RaftBusy from "../../RaftBusy.vue";
import raftGenericHelper from "../../../lib/raft_generic_helper";
import ChemtrackerSdsHelper from "../../../lib/ChemtrackerSdsHelper";

export default {
  name: "SdsSearchResultsListing",
  provide() {
    return {
      // The conditional is to handle jest.
      isB4: this.$isB4,
    };
  },
  data() {
    return {
      selectedSdsResult: null,
      fields: [
        { key: "selected", label: "Select", sortable: false },
        { key: "chemical_name", label: "Title", sortable: true },
        { key: "manufacturer_name", label: "Source", sortable: true },
        {
          key: "changed",
          label: "Document Date",
          sortable: true,
          formatter: (value) => raftGenericHelper.formatDateString(value, "/"),
        },
        { key: "", label: "Last Updated", sortable: true },
        {
          key: "origin",
          label: "Origin",
          sortable: true,
          formatter: (value) => {
            switch (value) {
              case "external":
                return "Centralized Database";
              case "file":
                return "Local Upload";
              case "url":
                return "Url";
              default:
                return "Unknown";
            }
          },
        },
        { key: "view_url", label: "", sortable: false },
      ],
      linkLoading: {},
    };
  },
  props: {
    sdsSearchResults: {
      required: true,
    },
    isActive: Boolean,
  },
  components: {
    RaftBusy,
  },
  computed: {
    ...mapGetters(["getSelectedSdsDocumentId"]),
    isBusy: function () {
      return !Array.isArray(this.sdsSearchResults);
    },
  },
  methods: {
    ...mapActions([
      "updateSelectedSdsDocumentId",
      "updateSelectedSdsEntityUuid",
      "updateSelectedSdsEntityType",
    ]),
    setSelectedSdsDocumentId: function (
      documentId,
      cachedEntityUuid,
      selectedItem,
    ) {
      this.$emit("reset-sds-selection", selectedItem);
      // The radio button value must be unique, so UUID is used when there is no
      // document ID. However, the rest of the code expects the document ID to
      // be 0 when it does not exist.
      const docId = documentId === cachedEntityUuid ? 0 : documentId;
      this.updateSelectedSdsDocumentId(docId);
      this.updateSelectedSdsEntityUuid(cachedEntityUuid);
      this.updateSelectedSdsEntityType(selectedItem.cached_entity_type);
    },
    getItemUniqueId(item) {
      if (item.cached_entity_uuid) {
        return item.cached_entity_uuid;
      }
      // Prevent document ID collisions with multiple providers.
      return item.provider + "__" + item.id;
    },
    getJwt(item) {
      if (item.origin !== "file") {
        return "";
      }
      const jwt = this.$store.getters.config.fileJwt;
      if (!jwt) {
        return "";
      }
      return "?jwt=" + jwt;
    },
    async sdsDownloadFile(item) {
      const uniqId = this.getItemUniqueId(item);
      if (this.linkLoading[uniqId]) {
        return;
      }
      // Use $set to make the property reactive.
      this.$set(this.linkLoading, uniqId, true);
      await ChemtrackerSdsHelper.sdsDownloadFile(item.provider, item.id, "");
      this.$set(this.linkLoading, uniqId, false);
    },
  },
};
</script>

<style scoped></style>
